import * as THREE from 'three';

/**
 * 三点建圆的归一化退化阈值。
 *
 * 说明：
 * 1. 三点外接圆使用的是二阶面积量，直接使用世界坐标绝对阈值会误伤小半径圆弧；
 * 2. 先把点归一化到单位量级后，该阈值只表达“几何上近似共线”，不再受模型尺寸影响。
 */
const NORMALIZED_CIRCLE_EPSILON = 1e-12;

/**
 * 根据三个 2D 点计算外接圆的圆心和半径。
 *
 * @param p1 第一个点。
 * @param p2 第二个点。
 * @param p3 第三个点。
 * @returns 外接圆的圆心和半径；三点共线或点集完全退化时返回 null。
 */
export function buildCircleFromThreePoints2D(
	p1: THREE.Vector2,
	p2: THREE.Vector2,
	p3: THREE.Vector2,
): { center: THREE.Vector2; radius: number } | null {
	const normalization = createCirclePointNormalization(p1, p2, p3);
	if (!normalization) {
		return null;
	}

	const normalizedCircle = buildNormalizedCircleFromThreePoints2D(
		normalizeCirclePoint(p1, normalization),
		normalizeCirclePoint(p2, normalization),
		normalizeCirclePoint(p3, normalization),
	);
	if (!normalizedCircle) {
		return null;
	}

	return {
		center: normalizedCircle.center.multiplyScalar(normalization.scale)
			.add(normalization.origin),
		radius: normalizedCircle.radius * normalization.scale,
	};
}

/**
 * 创建三点建圆的局部归一化配置。
 *
 * @param p1 第一个点。
 * @param p2 第二个点。
 * @param p3 第三个点。
 * @returns 可用归一化配置；三点完全重合时返回 null。
 */
function createCirclePointNormalization(
	p1: THREE.Vector2,
	p2: THREE.Vector2,
	p3: THREE.Vector2,
): { origin: THREE.Vector2; scale: number } | null {
	const origin = new THREE.Vector2(
		(p1.x + p2.x + p3.x) / 3,
		(p1.y + p2.y + p3.y) / 3,
	);
	const scale = Math.max(
		p1.distanceTo(origin),
		p2.distanceTo(origin),
		p3.distanceTo(origin),
	);

	if (scale <= Number.EPSILON) {
		return null;
	}

	return { origin, scale };
}

/**
 * 把点转换到局部归一化坐标。
 *
 * @param point 原始点。
 * @param normalization 归一化配置。
 * @returns 局部归一化点。
 */
function normalizeCirclePoint(
	point: THREE.Vector2,
	normalization: { origin: THREE.Vector2; scale: number },
): THREE.Vector2 {
	return point.clone().sub(normalization.origin).divideScalar(normalization.scale);
}

/**
 * 在归一化坐标中计算外接圆。
 *
 * @param p1 第一个归一化点。
 * @param p2 第二个归一化点。
 * @param p3 第三个归一化点。
 * @returns 归一化外接圆；三点共线时返回 null。
 */
function buildNormalizedCircleFromThreePoints2D(
	p1: THREE.Vector2,
	p2: THREE.Vector2,
	p3: THREE.Vector2,
): { center: THREE.Vector2; radius: number } | null {
	const ax = p1.x;
	const ay = p1.y;
	const bx = p2.x;
	const by = p2.y;
	const cx = p3.x;
	const cy = p3.y;
	const d = 2 * (ax * (by - cy) + bx * (cy - ay) + cx * (ay - by));

	if (Math.abs(d) < NORMALIZED_CIRCLE_EPSILON) {
		return null;
	}

	const ax2ay2 = ax * ax + ay * ay;
	const bx2by2 = bx * bx + by * by;
	const cx2cy2 = cx * cx + cy * cy;
	const ux = (ax2ay2 * (by - cy) + bx2by2 * (cy - ay) + cx2cy2 * (ay - by)) / d;
	const uy = (ax2ay2 * (cx - bx) + bx2by2 * (ax - cx) + cx2cy2 * (bx - ax)) / d;
	const center = new THREE.Vector2(ux, uy);

	return {
		center,
		radius: center.distanceTo(p1),
	};
}
